import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/shared/ui/Buttons'
import {
  Checkbox,
  DatePickerInput,
  FormProvider,
  TextInput,
} from '~/shared/ui/Form'
import { PlusIcon } from '~/shared/ui/Icons'
import { formSchema, FormValues } from '../EditForm/model'

type FormProps = {
  onSuccess: (formValues: FormValues) => void
  isSubmitting: boolean
}

export function Form({ onSuccess, isSubmitting }: FormProps) {
  const { t } = useTranslation()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  })

  const { watch, trigger, formState, setValue } = form
  const isTemporaryNumber = watch('isTemporaryNumber')

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || name !== 'isTemporaryNumber') return

      value.isTemporaryNumber
        ? setValue('issuedBy', '')
        : setValue('expirationDate', '')

      if (formState.submitCount) trigger()
    })

    return () => subscription.unsubscribe()
  }, [watch, setValue, formState.submitCount, trigger])

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Grid container spacing={2.5}>
        <Grid xs={7.5}>
          <TextInput
            label='form.license plate number'
            name='plateNumber'
            placeholder='form.enter license plate number'
            readOnly={isSubmitting}
          />
        </Grid>
        <Grid xs={4.5}>
          <Checkbox
            labelI18n='form.temporary plate'
            name='isTemporaryNumber'
            readOnly={isSubmitting}
            formControlProps={{ sx: { marginTop: '40px' } }}
          />
        </Grid>
        <Grid xs={12}>
          <TextInput
            label='form.number'
            name='number'
            placeholder='form.enter number'
            readOnly={isSubmitting}
          />
        </Grid>
        <Grid xs={12}>
          <DatePickerInput
            label='form.issue date'
            name='issuedDate'
            placeholder='form.select date'
            readOnly={isSubmitting}
          />
        </Grid>

        {isTemporaryNumber && (
          <Grid xs={12}>
            <DatePickerInput
              label='form.valid until'
              name='expirationDate'
              placeholder='form.select date'
              readOnly={isSubmitting}
            />
          </Grid>
        )}

        {!isTemporaryNumber && (
          <Grid xs={12}>
            <TextInput
              label='form.traffic police department code'
              name='issuedBy'
              placeholder='form.enter traffic police department code'
              readOnly={isSubmitting}
            />
          </Grid>
        )}
      </Grid>
      <br />
      <Button
        loading={isSubmitting}
        startDecorator={<PlusIcon />}
        fullWidth
        type='submit'
      >
        {t('button.add')}
      </Button>
    </FormProvider>
  )
}
